import { useCallback, useEffect, useRef } from 'react';
import { isDark, stitchById } from '../../domain/catalogs';
import type { EditorApi } from './useEditor';

function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || '#888';
}

export function ChartCanvas({
  editor,
  cellPx,
  onOpenCell,
}: {
  editor: EditorApi;
  cellPx: number;
  onOpenCell?: (r: number, c: number, clientX: number, clientY: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { cols, rows, cellsRef, yarn, tick } = editor;

  // gesture state
  const down = useRef<{ r: number; c: number; x: number; y: number } | null>(null);
  const moved = useRef(false);
  const striking = useRef(false);
  const lpFired = useRef(false);
  const lpTimer = useRef<number | undefined>(undefined);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = cols * cellPx;
    const h = rows * cellPx;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    const empty = cssVar('--surface-2');
    const line = cssVar('--border');
    const strong = cssVar('--border-strong');

    ctx.fillStyle = empty;
    ctx.fillRect(0, 0, w, h);

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cell = cellsRef.current[`${r}-${c}`];
        if (!cell) continue;
        const x = c * cellPx;
        const y = r * cellPx;
        const y0 = cell.color ? yarn(cell.color) : undefined;
        if (y0) {
          ctx.fillStyle = y0.hex;
          ctx.fillRect(x, y, cellPx, cellPx);
        }
        const st = cell.stitch ? stitchById(cell.stitch) : undefined;
        if (st?.sym) {
          ctx.fillStyle = y0 && isDark(y0.hex) ? 'rgba(255,255,255,.85)' : 'rgba(20,14,4,.8)';
          ctx.font = `700 ${Math.round(cellPx * 0.6)}px ui-monospace, monospace`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(st.sym, x + cellPx / 2, y + cellPx / 2 + 1);
        }
        // corner marker for cells carrying metadata
        if (cell.fiber || cell.weight || cell.note) {
          ctx.fillStyle = 'rgba(232,169,78,.9)';
          ctx.beginPath();
          ctx.moveTo(x + cellPx, y);
          ctx.lineTo(x + cellPx - Math.min(6, cellPx / 3), y);
          ctx.lineTo(x + cellPx, y + Math.min(6, cellPx / 3));
          ctx.fill();
        }
      }
    }

    ctx.strokeStyle = line;
    ctx.lineWidth = 1;
    for (let i = 0; i <= cols; i++) seg(ctx, i * cellPx + 0.5, 0, i * cellPx + 0.5, h);
    for (let i = 0; i <= rows; i++) seg(ctx, 0, i * cellPx + 0.5, w, i * cellPx + 0.5);
    ctx.strokeStyle = strong;
    ctx.lineWidth = 1.5;
    for (let i = 0; i <= cols; i += 5) seg(ctx, i * cellPx + 0.5, 0, i * cellPx + 0.5, h);
    for (let i = 0; i <= rows; i += 5) seg(ctx, 0, i * cellPx + 0.5, w, i * cellPx + 0.5);
  }, [cols, rows, cellPx, cellsRef, yarn]);

  useEffect(() => {
    draw();
  }, [draw, tick]);

  const cellFrom = (clientX: number, clientY: number) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const c = Math.floor((clientX - rect.left) / cellPx);
    const r = Math.floor((clientY - rect.top) / cellPx);
    if (r < 0 || c < 0 || r >= rows || c >= cols) return null;
    return { r, c };
  };

  return (
    <canvas
      ref={canvasRef}
      role="grid"
      aria-label={`${cols} by ${rows} knitting chart`}
      className="touch-none select-none rounded-lg"
      style={{ cursor: editor.tool === 'erase' ? 'cell' : editor.tool === 'eyedrop' ? 'copy' : 'crosshair' }}
      onContextMenu={(e) => {
        const pos = cellFrom(e.clientX, e.clientY);
        if (pos && onOpenCell) {
          e.preventDefault();
          onOpenCell(pos.r, pos.c, e.clientX, e.clientY);
        }
      }}
      onPointerDown={(e) => {
        if (e.button === 2) return;
        const pos = cellFrom(e.clientX, e.clientY);
        if (!pos) return;
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        down.current = { ...pos, x: e.clientX, y: e.clientY };
        moved.current = false;
        striking.current = false;
        lpFired.current = false;
        window.clearTimeout(lpTimer.current);
        lpTimer.current = window.setTimeout(() => {
          if (!down.current || striking.current) return;
          lpFired.current = true;
          onOpenCell?.(pos.r, pos.c, e.clientX, e.clientY);
        }, 450);
      }}
      onPointerMove={(e) => {
        if (!down.current) return;
        if (!moved.current && (Math.abs(e.clientX - down.current.x) > 6 || Math.abs(e.clientY - down.current.y) > 6)) {
          moved.current = true;
          window.clearTimeout(lpTimer.current);
          striking.current = true;
          editor.beginStroke();
          editor.strokeAt(down.current.r, down.current.c);
        }
        if (striking.current) {
          const pos = cellFrom(e.clientX, e.clientY);
          if (pos) editor.strokeAt(pos.r, pos.c);
        }
      }}
      onPointerUp={() => {
        window.clearTimeout(lpTimer.current);
        if (striking.current) editor.endStroke();
        else if (down.current && !lpFired.current) editor.tap(down.current.r, down.current.c);
        down.current = null;
        striking.current = false;
      }}
      onPointerCancel={() => {
        window.clearTimeout(lpTimer.current);
        if (striking.current) editor.endStroke();
        down.current = null;
        striking.current = false;
      }}
    />
  );
}

function seg(x: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number) {
  x.beginPath();
  x.moveTo(x1, y1);
  x.lineTo(x2, y2);
  x.stroke();
}
